import { useEffect, useMemo, useRef, useState } from "react";

import {
  useFacets,
  usePurgeRejected,
  useQuestions,
  useRetryFailed,
  useSplitDistribution,
  useStatusCounts,
  useSubmitJob,
  useTaxonomy,
  type PurgeRejectedResult,
  type QuestionFilters,
  type RetryFailedResult,
} from "@/api/queries";
import { useCan } from "@/auth/member";
import { QuestionDetail } from "@/components/question-detail";
import { SpeechChip } from "@/components/speech-chip";
import { Badge, StatusDot } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Drawer } from "@/components/ui/drawer";
import { Combobox, FieldLabel, Input } from "@/components/ui/input";
import { Callout, Empty, ErrorNote, Panel } from "@/components/ui/panel";
import { cn, formatScore } from "@/lib/utils";

/** DESIGN §14.2 — the whole bank, filtered. Server-side paging: §3 makes every query a
 *  network round trip, so fetching 50k rows to filter them in the browser is not an
 *  option that gets better later. */
const PAGE = 50;

const TH = "text-bank-muted px-3 py-2 font-mono text-[10.5px] font-medium tracking-[0.07em] uppercase";

/** DESIGN §17.11.6 — one control for the follow-up link and the split state, since "has a follow-up"
 *  and "needs splitting" are the two ends of the same question: is this one long, and what became of it. */
type LinkFilter = "" | "has_follow_up" | "is_follow_up" | "needs_split" | "proposal" | "split" | "exempt";

const LINK_FILTERS: Record<LinkFilter, Partial<QuestionFilters>> = {
  "": {},
  has_follow_up: { has_follow_up: true },
  is_follow_up: { is_follow_up: true },
  needs_split: { split_state: "needs_split" },
  proposal: { split_state: "proposal" },
  split: { split_state: "split" },
  exempt: { split_state: "exempt" },
};

const LINK_OPTIONS: { value: LinkFilter; label: string }[] = [
  { value: "", label: "any" },
  { value: "has_follow_up", label: "has a linked follow-up" },
  { value: "is_follow_up", label: "is someone's follow-up" },
  { value: "needs_split", label: "needs splitting" },
  { value: "proposal", label: "split proposal waiting" },
  { value: "split", label: "was split" },
  { value: "exempt", label: "long on purpose" },
];

export default function Questions() {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<string[]>([]);
  const [bucket, setBucket] = useState("");
  const [stack, setStack] = useState("");
  const [concept, setConcept] = useState("");
  const [difficulty, setDifficulty] = useState("");
  const [linkFilter, setLinkFilter] = useState<LinkFilter>("");
  const [offset, setOffset] = useState(0);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  // Set once retry-failed responds — this screen's equivalent of Review's PipelineNudge,
  // just summarising a sweep instead of one row's new stage (§10.2 has no single stage).
  const [retryResult, setRetryResult] = useState<RetryFailedResult | null>(null);

  const filters: QuestionFilters = {
    limit: PAGE,
    offset,
    ...(search ? { search } : {}),
    ...(status.length > 0 ? { status } : {}),
    ...(bucket ? { bucket } : {}),
    ...(stack ? { stack_path: stack } : {}),
    ...(concept ? { primary_concept: concept } : {}),
    ...(difficulty ? { difficulty_score: parseInt(difficulty, 10) } : {}),
    ...LINK_FILTERS[linkFilter],
  };
  const { data, isLoading, error, isPlaceholderData } = useQuestions(filters);
  const { data: distribution } = useSplitDistribution();
  const speechLimit = distribution?.limit_seconds ?? 6;
  const { data: counts } = useStatusCounts();
  const { data: facets } = useFacets();
  const { data: stackEntries } = useTaxonomy("stack");
  // A bucket is a property of a stack (§17.2); the list narrows by it on the server, but the
  // stack dropdown is narrowed here so it never offers a stack the bucket would empty out.
  const stackBuckets = useMemo(
    () => new Map((stackEntries ?? []).map((e) => [e.path, e.bucket ?? ""])),
    [stackEntries],
  );
  const buckets = useMemo(
    () => [...new Set([...stackBuckets.values()].filter((b) => b !== ""))].sort(),
    [stackBuckets],
  );
  const stackOptions = (facets?.stack_paths ?? []).filter(
    (s) => bucket === "" || stackBuckets.get(s) === bucket,
  );
  const retryFailed = useRetryFailed();
  const submitJob = useSubmitJob();
  const canRunPipeline = useCan("run_pipeline");
  const canPurge = useCan("purge_questions");
  const purge = usePurgeRejected();
  // What the open confirmation will delete: the ticked rows, or every rejected one.
  const [confirmPurge, setConfirmPurge] = useState<"selected" | "all" | null>(null);
  const [checked, setChecked] = useState<Set<number>>(new Set());
  const [purgeResult, setPurgeResult] = useState<PurgeRejectedResult | null>(null);

  // Stable identity: the tick-pruning effect below depends on it.
  const items = useMemo(() => data?.items ?? [], [data]);
  const total = data?.total ?? 0;
  const failedCount = counts?.counts?.failed ?? 0;
  const rejectedCount = counts?.counts?.rejected ?? 0;
  // Offered only while the list is narrowed to rejected and nothing else, so what the button
  // names is what is on screen — the server deletes every rejected row regardless of the other
  // filters, and a button that sat next to a mixed list would hide that.
  const showPurge = canPurge && rejectedCount > 0 && status.length === 1 && status[0] === "rejected";

  // Ticks belong to the page on screen: a row that left it (filter, page, or a purge) cannot
  // stay ticked, or the count would include rows the admin can no longer see.
  useEffect(() => {
    setChecked((current) => {
      const next = new Set([...current].filter((id) => items.some((q) => q.id === id)));
      return next.size === current.size ? current : next;
    });
  }, [items]);
  useEffect(() => {
    if (!showPurge) setChecked(new Set());
  }, [showPurge]);

  const allChecked = items.length > 0 && items.every((q) => checked.has(q.id));
  const someChecked = items.some((q) => checked.has(q.id));
  const selectAllRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (selectAllRef.current) selectAllRef.current.indeterminate = someChecked && !allChecked;
  }, [someChecked, allChecked]);

  function toggleOne(id: number) {
    setChecked((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function doPurge() {
    const ids = confirmPurge === "selected" ? [...checked] : undefined;
    purge.mutate(ids, {
      onSuccess: (result) => {
        setPurgeResult(result);
        setConfirmPurge(null);
        setChecked(new Set());
        // Deleting everything leaves nothing to look at under the rejected filter.
        if (ids === undefined) {
          setStatus([]);
          setOffset(0);
        }
      },
    });
  }

  function toggleStatus(value: string) {
    setOffset(0);
    setStatus((current) =>
      current.includes(value) ? current.filter((s) => s !== value) : [...current, value],
    );
  }

  function doRetryFailed() {
    retryFailed.mutate(undefined, { onSuccess: setRetryResult });
  }

  return (
    <div className="flex h-full flex-col gap-3 p-4">
      <header className="flex items-center gap-4">
        <h1 className="text-base font-semibold tracking-tight">Questions</h1>
        <span className="text-bank-muted font-mono text-xs tabular-nums">
          {isLoading ? "loading…" : `${total} live`}
        </span>
      </header>

      <Panel className="p-3">
        {/* Every filter resets the offset: page 7 of the old result set is a different
            set of rows once the filter changes, and landing there looks like data loss. */}
        <div className="grid gap-3 sm:grid-cols-[2fr_1fr_1fr_1fr_1fr_1fr]">
          <div>
            <FieldLabel htmlFor="q-search">Text contains</FieldLabel>
            <Input
              id="q-search"
              value={search}
              placeholder="closure, middleware, LEFT JOIN…"
              onChange={(e) => {
                setSearch(e.target.value);
                setOffset(0);
              }}
            />
          </div>
          <div>
            <FieldLabel htmlFor="q-bucket">Bucket</FieldLabel>
            <Combobox
              id="q-bucket"
              value={bucket}
              onChange={(v) => {
                setBucket(v);
                // A stack outside the new bucket would silently empty the list.
                if (stack && v !== "" && stackBuckets.get(stack) !== v) setStack("");
                setOffset(0);
              }}
              options={[{ value: "", label: "any" }, ...buckets.map((b) => ({ value: b, label: b }))]}
            />
          </div>
          <div>
            <FieldLabel htmlFor="q-stack">Stack</FieldLabel>
            <Combobox
              id="q-stack"
              value={stack}
              onChange={(v) => {
                setStack(v);
                setOffset(0);
              }}
              options={[{ value: "", label: "any" }, ...stackOptions.map((s) => ({ value: s, label: s }))]}
            />
          </div>
          <div>
            <FieldLabel htmlFor="q-concept">Concept</FieldLabel>
            <Combobox
              id="q-concept"
              value={concept}
              onChange={(v) => {
                setConcept(v);
                setOffset(0);
              }}
              options={[
                { value: "", label: "any" },
                ...(facets?.primary_concepts ?? []).map((c) => ({ value: c, label: c })),
              ]}
            />
          </div>
          <div>
            <FieldLabel htmlFor="q-diff">Difficulty</FieldLabel>
            <Combobox
              id="q-diff"
              value={difficulty}
              onChange={(v) => {
                setDifficulty(v);
                setOffset(0);
              }}
              options={[
                { value: "", label: "any" },
                { value: "1", label: "Level 1 (Foundational)" },
                { value: "2", label: "Level 2 (Working)" },
                { value: "3", label: "Level 3 (Proficient)" },
                { value: "4", label: "Level 4 (Advanced)" },
                { value: "5", label: "Level 5 (Mastery)" },
              ]}
            />
          </div>
          <div>
            <FieldLabel htmlFor="q-link">Follow-up / split</FieldLabel>
            <Combobox
              id="q-link"
              value={linkFilter}
              onChange={(v) => {
                setLinkFilter(v as LinkFilter);
                setOffset(0);
              }}
              options={LINK_OPTIONS}
            />
          </div>
        </div>
        {/* The facet carries the status dot rather than a whole Badge: a chip inside a
            button gives two pressed states arguing about which one you clicked. */}
        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          {Object.entries(counts?.counts ?? {})
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([name, n]) => {
              const on = status.includes(name);
              return (
                <button
                  key={name}
                  onClick={() => toggleStatus(name)}
                  aria-pressed={on}
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-md border px-2 py-1 font-mono text-[11px] transition-colors",
                    "focus-visible:ring-bank-accent focus-visible:ring-2 focus-visible:outline-none",
                    on
                      ? "border-bank-accent bg-bank-accent-wash text-bank-accent-ink"
                      : "border-bank-border text-bank-muted hover:bg-bank-surface-2",
                  )}
                >
                  <StatusDot value={name} />
                  {name}
                  <span className={cn("font-medium tabular-nums", !on && "text-bank-ink")}>{n}</span>
                </button>
              );
            })}
          {showPurge && (
            <>
              {checked.size > 0 && (
                <Button
                  size="sm"
                  variant="danger"
                  disabled={purge.isPending}
                  title="Permanently delete the ticked rejected questions — cannot be undone"
                  onClick={() => setConfirmPurge("selected")}
                  className="ml-1"
                >
                  Delete {checked.size} selected…
                </Button>
              )}
              <Button
                size="sm"
                variant="danger"
                disabled={purge.isPending}
                title="Permanently delete every rejected question — cannot be undone"
                onClick={() => setConfirmPurge("all")}
                className={checked.size > 0 ? undefined : "ml-1"}
              >
                Delete all {rejectedCount} rejected…
              </Button>
            </>
          )}
          {failedCount > 0 && canRunPipeline && (
            <Button
              size="sm"
              disabled={retryFailed.isPending}
              title="Reset failed questions and chunks below max_retries to the status they failed from (§10.2)"
              onClick={doRetryFailed}
              className="ml-1"
            >
              {retryFailed.isPending ? "Retrying…" : `Retry ${failedCount} failed`}
            </Button>
          )}
        </div>
      </Panel>

      <ErrorNote error={error ?? retryFailed.error ?? purge.error} />

      {confirmPurge !== null && showPurge && (
        <Callout tone="bad" className="flex flex-wrap items-center justify-between gap-2">
          <span>
            Permanently delete{" "}
            {confirmPurge === "selected" ? `the ${checked.size} selected` : `all ${rejectedCount}`} rejected
            question{(confirmPurge === "selected" ? checked.size : rejectedCount) === 1 ? "" : "s"}, with
            their history and comparison records? This cannot be undone. Rejected questions used in a
            golden-pair label are kept.
          </span>
          <div className="flex shrink-0 items-center gap-2">
            <Button size="sm" variant="danger" disabled={purge.isPending} onClick={doPurge}>
              {purge.isPending ? "Deleting…" : "Delete permanently"}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={purge.isPending}
              onClick={() => setConfirmPurge(null)}
            >
              Cancel
            </Button>
          </div>
        </Callout>
      )}

      {purgeResult && (
        <Callout tone="good" className="flex flex-wrap items-center justify-between gap-2">
          <span>
            Deleted {purgeResult.deleted} rejected question{purgeResult.deleted === 1 ? "" : "s"}
            {purgeResult.skipped_golden > 0
              ? `; ${purgeResult.skipped_golden} kept because a golden-pair label points at them`
              : ""}
            .
          </span>
          <Button size="sm" variant="ghost" onClick={() => setPurgeResult(null)}>
            Dismiss
          </Button>
        </Callout>
      )}

      {retryResult && (
        <Callout tone="attention" className="flex flex-wrap items-center justify-between gap-2">
          <span>
            {retryResult.questions} question{retryResult.questions === 1 ? "" : "s"}
            {retryResult.chunks > 0 ? ` and ${retryResult.chunks} chunk(s)` : ""} reset to retry
            {retryResult.exhausted > 0 ? `; ${retryResult.exhausted} past max_retries, left failed` : ""}.
            Returns here after the next pipeline run.
          </span>
          <div className="flex shrink-0 items-center gap-2">
            <Button
              size="sm"
              variant="primary"
              disabled={submitJob.isPending}
              onClick={() => submitJob.mutate("pipeline", { onSuccess: () => setRetryResult(null) })}
            >
              {submitJob.isPending ? "Queuing…" : "Run pipeline"}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setRetryResult(null)}>
              Dismiss
            </Button>
          </div>
        </Callout>
      )}

      <Panel className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="min-h-0 flex-1 overflow-auto">
          {items.length === 0 && !isLoading ? (
            <Empty>Nothing matches.</Empty>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-bank-surface-2 border-bank-border sticky top-0 border-b text-left">
                <tr>
                  {showPurge && (
                    <th className="w-8 px-3 py-2">
                      <input
                        ref={selectAllRef}
                        type="checkbox"
                        checked={allChecked}
                        onChange={() => setChecked(allChecked ? new Set() : new Set(items.map((q) => q.id)))}
                        aria-label="Select all rejected questions on this page"
                        className="accent-bank-accent size-3.5"
                      />
                    </th>
                  )}
                  <th className={TH}>#</th>
                  <th className={TH}>status</th>
                  <th className={TH}>question</th>
                  <th className={TH}>stack / concept</th>
                  <th className={TH}>type</th>
                  <th className={cn(TH, "text-right")}>diff</th>
                  <th className={cn(TH, "text-right")}>quality</th>
                  <th className={TH}>cluster</th>
                  <th className={TH}>follow-up</th>
                </tr>
              </thead>
              <tbody className={isPlaceholderData ? "opacity-60" : undefined}>
                {items.map((q) => (
                  <tr
                    key={q.id}
                    onClick={() => setSelectedId(q.id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setSelectedId(q.id);
                      }
                    }}
                    tabIndex={0}
                    role="button"
                    aria-label={`View question ${q.id} and its history`}
                    className={cn(
                      "border-bank-border hover:bg-bank-surface-2 focus-visible:outline-bank-accent cursor-pointer border-t transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2",
                      // A question in a follow-up pair is tinted, so the pair reads at a glance (§17.11.6).
                      (q.linked_follow_up || (q.follow_up_of ?? []).length > 0) && "bg-bank-accent-wash/40",
                    )}
                  >
                    {showPurge && (
                      // The row is a button that opens the drawer; the cell swallows its own
                      // clicks and keys so ticking a box does not also open it.
                      <td
                        className="w-8 px-3 py-2"
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={checked.has(q.id)}
                          onChange={() => toggleOne(q.id)}
                          aria-label={`Select question #${q.id}`}
                          className="accent-bank-accent size-3.5"
                        />
                      </td>
                    )}
                    <td className="text-bank-muted px-3 py-2 font-mono text-[11px] tabular-nums">{q.id}</td>
                    <td className="px-3 py-2">
                      <Badge value={q.status} />
                    </td>
                    <td className="question-text max-w-xl px-3 py-2 text-[13px]">
                      <div className="flex items-start gap-2">
                        <span className="min-w-0 flex-1">{q.question_text}</span>
                        <SpeechChip
                          seconds={q.speech_seconds}
                          limit={speechLimit}
                          className="mt-0.5 shrink-0"
                        />
                      </div>
                      {q.linked_follow_up && (
                        <p className="text-bank-muted mt-1 line-clamp-2 text-xs">
                          <span className="text-bank-accent-ink font-mono">
                            ↳ follow-up #{q.linked_follow_up.id}
                          </span>
                          {" · "}
                          {q.linked_follow_up.question_text}
                        </p>
                      )}
                    </td>
                    <td className="text-bank-muted px-3 py-2 font-mono text-[11px] leading-relaxed">
                      {q.stack_path ?? "—"}
                      <br />
                      {q.primary_concept ?? "—"}
                    </td>
                    <td className="text-bank-muted px-3 py-2 font-mono text-[11px]">
                      {q.question_type ?? "—"}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-[11px] tabular-nums">
                      {q.difficulty_score ?? "—"}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-[11px] tabular-nums">
                      {formatScore(q.quality_score)}
                    </td>
                    <td className="text-bank-muted px-3 py-2 font-mono text-[11px]">
                      {q.canonical_id
                        ? `→ #${q.canonical_id}`
                        : (q.cluster_size ?? 1) > 1
                          ? "canonical"
                          : "—"}
                    </td>
                    <td className="text-bank-muted px-3 py-2 font-mono text-[11px] leading-relaxed">
                      {q.linked_follow_up ? `→ #${q.linked_follow_up.id}` : null}
                      {q.linked_follow_up && (q.follow_up_of ?? []).length > 0 ? <br /> : null}
                      {(q.follow_up_of ?? []).length > 0
                        ? `↩ ${(q.follow_up_of ?? []).map((id) => `#${id}`).join(", ")}`
                        : null}
                      {!q.linked_follow_up && (q.follow_up_of ?? []).length === 0 ? "—" : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="border-bank-border flex items-center justify-between border-t px-3 py-2">
          <p className="text-bank-muted font-mono text-[11px] tabular-nums">
            {total === 0 ? "0" : `${offset + 1}–${Math.min(offset + PAGE, total)} of ${total}`}
          </p>
          <div className="flex gap-1.5">
            <Button size="sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>
              Previous
            </Button>
            <Button size="sm" disabled={offset + PAGE >= total} onClick={() => setOffset(offset + PAGE)}>
              Next
            </Button>
          </div>
        </div>
      </Panel>

      <Drawer
        open={selectedId !== null}
        onClose={() => setSelectedId(null)}
        title={selectedId !== null ? `Question #${selectedId}` : ""}
      >
        {selectedId !== null && (
          <QuestionDetail id={selectedId} showStatusActions onOpenQuestion={setSelectedId} />
        )}
      </Drawer>
    </div>
  );
}
